import "@arco-design/web-react/es/Trigger/style";
import _Trigger from "@arco-design/web-react/es/Trigger";
import "@arco-design/web-react/es/Select/style";
import _Select from "@arco-design/web-react/es/Select";
import "@arco-design/web-react/es/Form/style";
import _Form from "@arco-design/web-react/es/Form";
import "@arco-design/web-react/es/Table/style";
import _Table from "@arco-design/web-react/es/Table";
import "@arco-design/web-react/es/Button/style";
import _Button from "@arco-design/web-react/es/Button";
import "@arco-design/web-react/es/Input/style";
import _Input from "@arco-design/web-react/es/Input";
import { useEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React from 'react';
import { useDebounceEffect, useRequest, useSetState } from 'ahooks';
import { getPersonPage } from '@/services';
import styles from './index.module.less';
function toOption(person) {
  const value = person.userId || person.id;
  return {
    label: person.name || person.username || person.id,
    value,
    person
  };
}
function getPersonValue(person) {
  return person.userId || person.id;
}
function normalizeValue(value, valueFormat = 'array') {
  if (Array.isArray(value)) {
    return value;
  }
  if (!value) {
    return [];
  }
  if (valueFormat === 'comma') {
    return String(value).split(',').map(item => item.trim()).filter(Boolean);
  }
  return [value];
}
function PersonSelector(props) {
  const {
    value,
    options,
    onChange,
    type = 'radio',
    disabled = false,
    size = 'default',
    bordered = true,
    placeholder = '请选择人员',
    allowClear = true,
    departId,
    roleCode,
    popupWidth = 500,
    valueFormat = 'array',
    labelKey,
    deptNameKey,
    form
  } = props;
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [selectedMap, setSelectedMap] = useState(new Map());
  const selectedMapRef = useRef(selectedMap);
  const fetchForInitRef = useRef(false);
  const [queryState, setQueryState] = useSetState({
    page: 1,
    pageSize: 20,
    departId,
    name: '',
    roleCode
  });
  const personPageApi = useRequest(getPersonPage, {
    manual: true
  });

  // 选中人员时回写 labelKey / deptNameKey 到父级 Form
  const writeLabelFields = option => {
    if (!form) return;
    if (labelKey && typeof labelKey === 'string') {
      form.setFieldValue(labelKey, option?.label || option?.person?.name || '');
    }
    if (deptNameKey && typeof deptNameKey === 'string') {
      form.setFieldValue(deptNameKey, option?.person?.deptName || '');
    }
  };
  useEffect(() => {
    selectedMapRef.current = selectedMap;
  }, [selectedMap]);
  useEffect(() => {
    setQueryState({
      page: 1,
      departId,
      roleCode
    });
  }, [departId, roleCode, setQueryState]);
  useEffect(() => {
    const nextMap = new Map();
    const nextIds = normalizeValue(value, valueFormat);
    const providedOptions = options || [];
    const previousMap = selectedMapRef.current;
    nextIds.forEach(id => {
      const matched = providedOptions.find(item => item.value === id);
      if (matched) {
        nextMap.set(id, matched);
      } else {
        const previous = previousMap.get(id);
        if (previous) {
          nextMap.set(id, previous);
          return;
        }
        nextMap.set(id, {
          value: id,
          label: id,
          person: {
            id: '',
            name: id || '',
            deptName: matched?.person?.deptName || '',
            userId: id
          }
        });
      }
    });
    setSelectedMap(nextMap);
  }, [options, value, valueFormat]);
  useDebounceEffect(() => {
    if (!open) {
      return;
    }
    personPageApi.run({
      page: queryState.page,
      pageSize: queryState.pageSize,
      departId: queryState.departId,
      name: queryState.name,
      roleCode: queryState.roleCode
    });
  }, [open, queryState.page, queryState.pageSize, queryState.departId, queryState.name, queryState.roleCode], {
    wait: 80
  });
  const selectedOptions = useMemo(() => Array.from(selectedMap.values()), [selectedMap]);
  const selectValue = useMemo(() => selectedOptions.map(item => item.value), [selectedOptions]);
  const triggerChange = nextMap => {
    const nextOptions = Array.from(nextMap.values());
    const nextValues = nextOptions.map(item => item.value);
    // 按 valueFormat 决定写入形态：array 模式给数组，comma 模式给拼接字符串。
    // 之前固定 join(',')，跟 valueFormat='array' 矛盾：form 拿到字符串后，
    // 下次 normalizeValue(value, 'array') 会把它当单值处理，导致 selectedMap
    // 错位（多选时只显示第一项，其余消失）。
    const payload = valueFormat === 'array' ? nextValues : nextValues.join(',');
    onChange?.(payload, nextOptions);
    // 回写 labelKey / deptNameKey：
    // - 单选：写唯一一项的 label。
    // - 多选：把所有人的 name 用逗号拼接，便于后端按字符串存。
    if (form) {
      if (type === 'radio') {
        writeLabelFields(nextOptions[0]);
      } else if (labelKey && typeof labelKey === 'string') {
        const names = nextOptions.map(o => o?.label || o?.person?.name || '').filter(Boolean).join(',');
        form.setFieldValue(labelKey, names);
        if (deptNameKey && typeof deptNameKey === 'string') {
          const deptNames = nextOptions.map(o => o?.person?.deptName || '').filter(Boolean).join(',');
          form.setFieldValue(deptNameKey, deptNames);
        }
      }
    }
  };
  const columns = [{
    title: '姓名',
    dataIndex: 'name',
    render: value => value || '-'
  }, {
    title: '部门',
    dataIndex: 'deptName',
    render: value => value || '-'
  }];
  const popup = /*#__PURE__*/React.createElement("div", {
    className: styles.popup,
    style: {
      width: typeof popupWidth === 'number' ? `${popupWidth}px` : popupWidth
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.toolbar
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.searchForm
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.searchField
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.searchLabel
  }, "\u59D3\u540D"), /*#__PURE__*/React.createElement(_Input, {
    allowClear: true,
    value: keyword,
    placeholder: "\u8BF7\u8F93\u5165\u59D3\u540D",
    style: {
      width: 220
    },
    onChange: setKeyword,
    onPressEnter: () => {
      setQueryState({
        page: 1,
        name: keyword
      });
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.actionGroup
  }, /*#__PURE__*/React.createElement(_Button, {
    type: "primary",
    onClick: () => {
      setQueryState({
        page: 1,
        name: keyword
      });
    }
  }, "\u67E5\u8BE2"), /*#__PURE__*/React.createElement(_Button, {
    onClick: () => {
      setKeyword('');
      setQueryState({
        page: 1,
        name: ''
      });
    }
  }, "\u91CD\u7F6E")))), /*#__PURE__*/React.createElement("div", {
    className: styles.tableWrap
  }, /*#__PURE__*/React.createElement(_Table, {
    rowKey: getPersonValue,
    size: "small",
    columns: columns,
    data: personPageApi.data?.list || [],
    loading: personPageApi.loading,
    scroll: {
      y: 280
    },
    pagination: {
      current: queryState.page,
      pageSize: queryState.pageSize,
      total: personPageApi.data?.totalRow || 0,
      showTotal: true,
      onChange: (page, pageSize) => {
        setQueryState({
          page,
          pageSize
        });
      }
    },
    rowSelection: {
      type,
      selectedRowKeys: selectValue,
      onSelect: (selected, record) => {
        const nextMap = new Map(selectedMapRef.current);
        const option = toOption(record);
        const recordValue = getPersonValue(record);
        if (type === 'radio') {
          nextMap.clear();
          if (selected) {
            nextMap.set(recordValue, option);
          }
        } else if (selected) {
          nextMap.set(recordValue, option);
        } else {
          nextMap.delete(recordValue);
        }
        selectedMapRef.current = nextMap;
        setSelectedMap(nextMap);
        triggerChange(nextMap);
      },
      onSelectAll: (selected, selectedRows) => {
        if (type === 'radio') {
          return;
        }
        const nextMap = new Map();
        if (selected) {
          selectedRows.forEach(item => {
            nextMap.set(getPersonValue(item), toOption(item));
          });
        }
        selectedMapRef.current = nextMap;
        setSelectedMap(nextMap);
        triggerChange(nextMap);
      }
    }
  })));

  // value 变化时反查缺失的用户信息，保证回显正确
  useEffect(() => {
    const ids = normalizeValue(value, valueFormat);
    if (!ids.length) return;
    const missingIds = ids.filter(id => !selectedMapRef.current.has(id));
    setTimeout(() => {
      if (!missingIds.length) return;

      // 有 labelKey 时，尝试从表单中读取名称直接回显，跳过接口请求
      const labelFromForm = labelKey ? form?.getFieldValue(labelKey) : '';
      const deptNameFromForm = deptNameKey ? form?.getFieldValue(deptNameKey) : '';
      if (labelKey && labelFromForm) {
        // 当 valueFormat='comma' 时，labelFromForm 也是逗号拼接的 names，
        // 按位置对齐：ids[i] 对应 labelFromForm.split(',')[i]。
        // 这样多选回显时每个 tag 显示自己的姓名，而不是全员共用同一字符串。
        const labels = labelFromForm.split(',').map(s => s.trim());
        const deptNames = deptNameFromForm ? deptNameFromForm.split(',').map(s => s.trim()) : [];
        const idsList = normalizeValue(value, valueFormat);
        const nextMap = new Map(selectedMapRef.current);
        idsList.forEach((id, idx) => {
          nextMap.set(id, {
            value: id,
            label: labels[idx] || id,
            person: {
              name: labels[idx] || id,
              userId: id,
              deptName: deptNames[idx] || undefined
            }
          });
        });
        selectedMapRef.current = nextMap;
        setSelectedMap(new Map(nextMap));
        return;
      }
      fetchForInitRef.current = true;
    });
  }, [value, valueFormat, labelKey, deptNameKey, form]);

  // personPage 返回后填充 selectedMap（仅初始化回显时）
  useEffect(() => {
    if (!fetchForInitRef.current) return;
    if (!personPageApi.data?.list?.length) return;
    const nextMap = new Map(selectedMapRef.current);
    personPageApi.data.list.forEach(person => {
      const key = person.userId || person.id || '';
      if (!key) return;
      nextMap.set(key, {
        value: key,
        label: person.name || key,
        person
      });
    });
    selectedMapRef.current = nextMap;
    setSelectedMap(new Map(nextMap));
    fetchForInitRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personPageApi.data]);
  return /*#__PURE__*/React.createElement(_Trigger, {
    trigger: "click",
    popup: () => popup,
    position: "bl",
    duration: 0,
    popupAlign: {
      bottom: 4
    },
    popupVisible: open,
    getPopupContainer: node => {
      const modalWrapperElement = node.closest('.arco-modal-wrapper');
      return modalWrapperElement || document.body;
    },
    onVisibleChange: setOpen,
    disabled: disabled,
    unmountOnExit: false
  }, /*#__PURE__*/React.createElement("div", null, labelKey && /*#__PURE__*/React.createElement(_Form.Item, {
    hidden: true,
    field: labelKey
  }, /*#__PURE__*/React.createElement(_Input, null)), deptNameKey && /*#__PURE__*/React.createElement(_Form.Item, {
    hidden: true,
    field: deptNameKey
  }, /*#__PURE__*/React.createElement(_Input, null)), /*#__PURE__*/React.createElement(_Select, {
    mode: 'multiple',
    value: selectValue,
    options: selectedOptions,
    allowClear: allowClear,
    disabled: disabled,
    size: size,
    bordered: bordered,
    placeholder: placeholder,
    style: {
      width: '100%'
    },
    triggerProps: {
      popupVisible: false
    },
    onClear: () => {
      const nextMap = new Map();
      selectedMapRef.current = nextMap;
      setSelectedMap(nextMap);
      triggerChange(nextMap);
    },
    onChange: nextValue => {
      const valueList = normalizeValue(Array.isArray(nextValue) ? nextValue : nextValue, valueFormat);
      const nextMap = new Map();
      valueList.forEach(id => {
        const matched = selectedMapRef.current.get(id);
        if (matched) {
          nextMap.set(id, matched);
        }
      });
      selectedMapRef.current = nextMap;
      setSelectedMap(nextMap);
      triggerChange(nextMap);
    }
  })));
}
export default PersonSelector;